import React, { useMemo } from "react";
import {
  LatestNewsCurtainCard,
  Thumbnail,
  RotatingAgents,
} from "../../components";
import { MdAlternateEmail } from "react-icons/md";
import "./About.css";

/*
Disabled for now, but kept here for later reuse.
import { FaRegCalendarDays } from "react-icons/fa6";
import { MdClose } from "react-icons/md";
*/

const TEAM_INSTAGRAM_URL =
  "https://www.instagram.com/thefirstnoncarbonartist/";

/*
const NETWORK_CORNER_RADIUS = 20;
const PERIMETER_TRACE_DURATION = 760;
const WIRE_TRACE_DELAY = 660;
const WIRE_TRACE_DURATION = 420;
const MODAL_REVEAL_DELAY = 1180;
const SEQUENCE_RESET_DELAY = 1500;

const normalizeRect = (rect, containerRect) => ({
  left: rect.left - containerRect.left,
  top: rect.top - containerRect.top,
  width: rect.width,
  height: rect.height,
});

const buildCardBeamPaths = (rect, side) => {
  const left = rect.left;
  const right = rect.left + rect.width;
  const top = rect.top;
  const bottom = rect.top + rect.height;
  const midY = top + rect.height / 2;
  const radius = Math.min(
    NETWORK_CORNER_RADIUS,
    rect.width * 0.14,
    rect.height * 0.09,
  );

  if (side === "left") {
    return {
      innerPoint: { x: right, y: midY },
      upPath: `M ${left} ${midY} L ${left} ${top + radius} Q ${left} ${top} ${left + radius} ${top} L ${right - radius} ${top} Q ${right} ${top} ${right} ${top + radius} L ${right} ${midY}`,
      downPath: `M ${left} ${midY} L ${left} ${bottom - radius} Q ${left} ${bottom} ${left + radius} ${bottom} L ${right - radius} ${bottom} Q ${right} ${bottom} ${right} ${bottom - radius} L ${right} ${midY}`,
    };
  }

  return {
    innerPoint: { x: left, y: midY },
    upPath: `M ${right} ${midY} L ${right} ${top + radius} Q ${right} ${top} ${right - radius} ${top} L ${left + radius} ${top} Q ${left} ${top} ${left} ${top + radius} L ${left} ${midY}`,
    downPath: `M ${right} ${midY} L ${right} ${bottom - radius} Q ${right} ${bottom} ${right - radius} ${bottom} L ${left + radius} ${bottom} Q ${left} ${bottom} ${left} ${bottom - radius} L ${left} ${midY}`,
  };
};

const buildWirePath = (from, to, radius = 0) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.hypot(dx, dy) || 1;
  const endX = to.x - (dx / distance) * Math.max(radius, 0);
  const endY = to.y - (dy / distance) * Math.max(radius, 0);
  const spanX = endX - from.x;
  const spanY = endY - from.y;
  const controlYOffset = spanY * 0.08;
  const controlOneX = from.x + spanX * 0.42;
  const controlTwoX = endX - spanX * 0.28;
  const controlOneY = from.y + controlYOffset;
  const controlTwoY = endY - controlYOffset;

  return `M ${from.x} ${from.y} C ${controlOneX} ${controlOneY} ${controlTwoX} ${controlTwoY} ${endX} ${endY}`;
};
*/

const About = ({
  preloadedTeamMembers = [],
  teamError = null,
  preloadedNews = null,
  newsError = null,
}) => {
  const teamMembers = preloadedTeamMembers.map((member) => ({
    title: member.name,
    description: member.description,
    image: member.image_url,
    site_name: member.site_name,
    url: member.external_url,
    linkedinUrl: member.external_url,
    instagramUrl: TEAM_INSTAGRAM_URL,
  }));

  /*
  const primaryTeamMembers = teamMembers.slice(0, 2);
  const extraTeamMembers = teamMembers.slice(2);
  const teamNetworkRef = useRef(null);
  const leftCardRef = useRef(null);
  const rightCardRef = useRef(null);
  const calendarButtonRef = useRef(null);
  const sequenceTimeoutsRef = useRef([]);
  const [teamNetworkGeometry, setTeamNetworkGeometry] = useState(null);
  const [isMeetingSequenceActive, setIsMeetingSequenceActive] = useState(false);
  const [meetingSequenceKey, setMeetingSequenceKey] = useState(0);
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const hasTeamNetwork = primaryTeamMembers.length === 2;

  const clearSequenceTimers = useCallback(() => {
    sequenceTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    sequenceTimeoutsRef.current = [];
  }, []);

  const sortedNews = useMemo(() => {
    const items = Array.isArray(preloadedNews) ? [...preloadedNews] : [];

    return items.sort((a, b) => {
      const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return bTime - aTime;
    });
  }, [preloadedNews]);

  const measureTeamNetwork = useCallback(() => {
    if (
      !teamNetworkRef.current ||
      !leftCardRef.current ||
      !rightCardRef.current ||
      !calendarButtonRef.current
    ) {
      return;
    }

    const containerRect = teamNetworkRef.current.getBoundingClientRect();
    const leftRect = normalizeRect(
      leftCardRef.current.getBoundingClientRect(),
      containerRect,
    );
    const rightRect = normalizeRect(
      rightCardRef.current.getBoundingClientRect(),
      containerRect,
    );
    const buttonRect = normalizeRect(
      calendarButtonRef.current.getBoundingClientRect(),
      containerRect,
    );

    setTeamNetworkGeometry({
      width: containerRect.width,
      height: containerRect.height,
      leftRect,
      rightRect,
      buttonCenter: {
        x: buttonRect.left + buttonRect.width / 2,
        y: buttonRect.top + buttonRect.height / 2,
      },
      buttonRadius: Math.min(buttonRect.width, buttonRect.height) / 2,
    });
  }, []);

  useEffect(() => {
    if (!hasTeamNetwork) return undefined;

    measureTeamNetwork();

    let resizeObserver;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        measureTeamNetwork();
      });

      [
        teamNetworkRef.current,
        leftCardRef.current,
        rightCardRef.current,
        calendarButtonRef.current,
      ]
        .filter(Boolean)
        .forEach((node) => resizeObserver.observe(node));
    }

    window.addEventListener("resize", measureTeamNetwork);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measureTeamNetwork);
    };
  }, [hasTeamNetwork, measureTeamNetwork]);

  useEffect(() => clearSequenceTimers, [clearSequenceTimers]);

  useEffect(() => {
    if (!isMeetingModalOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMeetingModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [isMeetingModalOpen]);

  const teamNetworkPaths = useMemo(() => {
    if (!teamNetworkGeometry) return null;

    const leftCardPaths = buildCardBeamPaths(teamNetworkGeometry.leftRect, "left");
    const rightCardPaths = buildCardBeamPaths(
      teamNetworkGeometry.rightRect,
      "right",
    );

    return {
      width: teamNetworkGeometry.width,
      height: teamNetworkGeometry.height,
      leftCardPaths,
      rightCardPaths,
      leftWirePath: buildWirePath(
        leftCardPaths.innerPoint,
        teamNetworkGeometry.buttonCenter,
        teamNetworkGeometry.buttonRadius,
      ),
      rightWirePath: buildWirePath(
        rightCardPaths.innerPoint,
        teamNetworkGeometry.buttonCenter,
        teamNetworkGeometry.buttonRadius,
      ),
      buttonCenter: teamNetworkGeometry.buttonCenter,
      buttonRadius: teamNetworkGeometry.buttonRadius,
    };
  }, [teamNetworkGeometry]);

  const handleCalendarClick = useCallback(() => {
    if (isMeetingSequenceActive) return;

    clearSequenceTimers();
    setIsMeetingModalOpen(false);
    setMeetingSequenceKey((current) => current + 1);
    setIsMeetingSequenceActive(true);

    sequenceTimeoutsRef.current = [
      window.setTimeout(() => {
        setIsMeetingModalOpen(true);
      }, MODAL_REVEAL_DELAY),
      window.setTimeout(() => {
        setIsMeetingSequenceActive(false);
      }, SEQUENCE_RESET_DELAY),
    ];
  }, [clearSequenceTimers, isMeetingSequenceActive]);
  */

  const sortedNews = useMemo(() => {
    const items = Array.isArray(preloadedNews) ? [...preloadedNews] : [];

    return items.sort((a, b) => {
      const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return bTime - aTime;
    });
  }, [preloadedNews]);

  /*
  const renderTeamMeetingNetwork = () => (
    <>
      <div ref={teamNetworkRef} className="team-network">
        {teamNetworkPaths ? (
          <svg
            className="team-network__overlay"
            viewBox={`0 0 ${teamNetworkPaths.width} ${teamNetworkPaths.height}`}
            aria-hidden="true"
          >
            <path
              className="team-network__wire"
              d={teamNetworkPaths.leftWirePath}
            />
            <path
              className="team-network__wire"
              d={teamNetworkPaths.rightWirePath}
            />

            {isMeetingSequenceActive ? (
              <g key={meetingSequenceKey}>
                <path
                  className="team-network__beam"
                  d={teamNetworkPaths.leftCardPaths.upPath}
                  pathLength="1"
                  style={{
                    "--beam-delay": "0ms",
                    "--beam-duration": `${PERIMETER_TRACE_DURATION}ms`,
                  }}
                />
                <path
                  className="team-network__beam"
                  d={teamNetworkPaths.leftCardPaths.downPath}
                  pathLength="1"
                  style={{
                    "--beam-delay": "0ms",
                    "--beam-duration": `${PERIMETER_TRACE_DURATION}ms`,
                  }}
                />
                <path
                  className="team-network__beam"
                  d={teamNetworkPaths.rightCardPaths.upPath}
                  pathLength="1"
                  style={{
                    "--beam-delay": "0ms",
                    "--beam-duration": `${PERIMETER_TRACE_DURATION}ms`,
                  }}
                />
                <path
                  className="team-network__beam"
                  d={teamNetworkPaths.rightCardPaths.downPath}
                  pathLength="1"
                  style={{
                    "--beam-delay": "0ms",
                    "--beam-duration": `${PERIMETER_TRACE_DURATION}ms`,
                  }}
                />
                <path
                  className="team-network__beam team-network__beam--wire"
                  d={teamNetworkPaths.leftWirePath}
                  pathLength="1"
                  style={{
                    "--beam-delay": `${WIRE_TRACE_DELAY}ms`,
                    "--beam-duration": `${WIRE_TRACE_DURATION}ms`,
                  }}
                />
                <path
                  className="team-network__beam team-network__beam--wire"
                  d={teamNetworkPaths.rightWirePath}
                  pathLength="1"
                  style={{
                    "--beam-delay": `${WIRE_TRACE_DELAY}ms`,
                    "--beam-duration": `${WIRE_TRACE_DURATION}ms`,
                  }}
                />
                <circle
                  className="team-network__button-pulse"
                  cx={teamNetworkPaths.buttonCenter.x}
                  cy={teamNetworkPaths.buttonCenter.y}
                  r={Math.max(teamNetworkPaths.buttonRadius * 0.38, 12)}
                  style={{
                    "--button-delay": `${WIRE_TRACE_DELAY + WIRE_TRACE_DURATION - 40}ms`,
                  }}
                />
              </g>
            ) : null}
          </svg>
        ) : null}

        <div
          ref={leftCardRef}
          className="team-network__card-slot team-network__card-slot--left"
        >
          <Thumbnail
            ogData={primaryTeamMembers[0]}
            variant="team-member"
          />
        </div>

        <div className="team-network__button-slot">
          <button
            ref={calendarButtonRef}
            type="button"
            className={`team-calendar-button ${
              isMeetingSequenceActive ? "is-active" : ""
            }`}
            onClick={handleCalendarClick}
            aria-haspopup="dialog"
            aria-expanded={isMeetingModalOpen}
            aria-label="Open team meeting scheduler"
          >
            <FaRegCalendarDays className="team-calendar-button__icon" />
          </button>
        </div>

        <div
          ref={rightCardRef}
          className="team-network__card-slot team-network__card-slot--right"
        >
          <Thumbnail
            ogData={primaryTeamMembers[1]}
            variant="team-member"
          />
        </div>
      </div>

      {extraTeamMembers.length > 0 ? (
        <div className="team-container team-container--extras">
          {extraTeamMembers.map((member, index) => (
            <Thumbnail
              key={`extra-team-${member.title || index}`}
              ogData={member}
              variant="team-member"
            />
          ))}
        </div>
      ) : null}
    </>
  );

  const renderMeetingSchedulerModal = () => (
    <div
      className="meeting-modal"
      onClick={() => setIsMeetingModalOpen(false)}
    >
      <div
        className="meeting-modal__card"
        role="dialog"
        aria-modal="true"
        aria-label="Schedule a meeting"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="meeting-modal__close"
          onClick={() => setIsMeetingModalOpen(false)}
          aria-label="Close meeting modal"
        >
          <MdClose />
        </button>
        <div className="meeting-modal__body" />
      </div>
    </div>
  );
  */

  return (
    <div className="about-wrapper">
      <div className="about-page">
        <div className="about-content">
          <div className="about-section agents-section">
            <RotatingAgents />
          </div>

          <div className="about-section team-section">
            {teamMembers && teamMembers.length > 0 ? (
              <div className="team-container">
                {teamMembers.map((member, index) => (
                  <Thumbnail
                    key={`team-member-${member.title || index}`}
                    ogData={member}
                    variant="team-member"
                  />
                ))}
              </div>
            ) : teamError ? (
              <div>Error loading team: {teamError}</div>
            ) : (
              <div>Loading team...</div>
            )}
          </div>

          <div className="about-section news-section">
            {sortedNews.length > 0 ? (
              <div className="news-container">
                {sortedNews.map((item, index) =>
                  index === 0 ? (
                    <LatestNewsCurtainCard
                      key={`${item.id || item.title || "latest-news"}-${index}`}
                      item={item}
                    />
                  ) : (
                    <Thumbnail
                      key={`${item.id || item.title || "news"}-${index}`}
                      ogData={item}
                      variant="team"
                    />
                  ),
                )}
              </div>
            ) : newsError ? (
              <div>Error loading news: {newsError}</div>
            ) : (
              <div>Loading news...</div>
            )}
          </div>

          <div className="about-section contact-section">
            <a
              href="mailto:thefirstnoncarbonartist@gmail.com"
              className="email-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <MdAlternateEmail className="email-icon" />
              <span>thefirstnoncarbonartist@gmail.com</span>
            </a>
          </div>

          {/* Instagram Widget */}
          {/* <div className="iframe-scale-wrapper">
            <iframe src="https://widgets.sociablekit.com/instagram-reels/iframe/25602750"></iframe>
          </div> */}
        </div>
      </div>

      {/* Meeting scheduler modal disabled together with the calendar button. */}
    </div>
  );
};

export default About;
